import React, { useEffect, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import Swal from 'sweetalert2'
import { setBreadcrumbItems } from "../../store/actions";
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { handleErrors, showToaster } from "components/Common/common";
import { PAGECODE, PAGEPATHS, USERTYPES, USER_STATUS } from "components/Common/Constant";
import EditModal from "./EditModal";
import AddModal from "./AddModal";
import { useSelector } from "react-redux";
import { ModernButton, ModernCard, ModernBadge, ModernStatCard } from '../../components/ModernUI';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Shield,
  Users,
  Mail,
  Building2,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  Ban
} from 'lucide-react';

const UserManagement = (props) => {
  document.title = "User Management | GOVCOM";
  const navigate = useNavigate();
  const [modal, setModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editUserData, setEditUserData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isModifying, setIsModifying] = useState(false);
  const [users, setUsers] = useState([]);
  const [addUserData, setAddUserData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    userType: '',
    password: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  useEffect(() => {
    props.setBreadcrumbItems('User Management', [])
  })

  const user = useSelector((state) => state?.users.users)

  const handleClick = async (type, userData) => {
    console.log('INITIAL - userData:', userData);

    try {
      if (type === 1) {
        setModal(true)
        setEditUserData(userData)
        return
      }
      if (type === 2) {
        const custom = '<div style="display: flex; flex-direction: column;"><i class="mdi mdi-trash-can-outline" style="font-size: 4em; color:rgb(236, 111, 111);"></i> <span>Are you sure?</span></div>'
        Swal.fire({
          title: custom,
          html: "You won't be able to undo this!",
          showCancelButton: true,
          confirmButtonColor: "#6f7bec",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete!"
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              const { data } = await axiosInstance.delete(`${ApiNames.USER_DELETE}/${userData._id}`)
              setUsers((prev) => prev.filter(u => u._id !== userData._id))
              showToaster(data.status, data.message)
            } catch (error) {
              handleErrors(error)
            }
          }
        });
      }
      if (type === 3) {
        console.log('userData:', userData);
        const permissions = !userData.permissions
          ? { read: [], write: [] }
          : userData.permissions
        console.log('permissions:', permissions);
        navigate(PAGEPATHS.Accessmanage,
          {
            state: {
              user: { ...userData, permissions }
            }
          })
      }
      if (type === 4) { // Approve
        const { _id, __v, ...others } = userData;
        // Optimistic update or just call API and refresh
        try {
          const update = { status: USER_STATUS.Active };
          const { data } = await axiosInstance.put(
            `${ApiNames.USER_UPDATE}/${_id}`,
            update,
            {
              headers: {
                'If-Match': __v,
                'Content-Type': 'application/json',
              },
            }
          )
          showToaster(data.status, "User approved successfully");
          getUserList();
        } catch (error) {
          handleErrors(error);
        }
      }
    } catch (error) {
      handleErrors(error)
    }
  }

  const getUserList = async () => {
    try {
      const endpoint = user.userType === USERTYPES.Admin
        ? ApiNames.USER_LIST
        : ApiNames.ORGANIZATION_LIST

      const { data } = await axiosInstance.get(endpoint)
      const validData = data.data.map(v => ({
        _id: v._id,
        firstname: v.firstname,
        lastname: v.lastname,
        name: v.firstname ? `${v.firstname} ${v.lastname}` : '-',
        email: v.email,
        organizationId: v.organizationId,
        branch: v.branch,
        userType: v.userType,
        status: v.status,
        permissions: v.permissions,
        __v: v.__v
      }))

      setUsers(validData)
    } catch (e) {
      handleErrors(e)
    }
  }

  useEffect(() => {
    getUserList()
  }, [user])

  const handleEditSubmit = async (e) => {
    try {
      e.preventDefault();
      setIsModifying(true)
      const { _id, __v, permissions, organizationId, ...update } = editUserData
      const { data } = await axiosInstance.put(
        `${ApiNames.USER_UPDATE}/${_id}`,
        update,
        {
          headers: {
            'If-Match': __v,
            'Content-Type': 'application/json',
          },
        }
      )
      setModal(false)
      getUserList()
      setIsModifying(false)
      showToaster(data.status, data.message)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsModifying(false)
    }
  };

  const handleAddSubmit = async (e) => {
    try {
      e.preventDefault()
      setIsModifying(true)
      const { data } = await axiosInstance.post(ApiNames.ADD_USER, addUserData)
      if (data.status) {
        getUserList()
        setIsModifying(false)
        setAddModal(false)
        setAddUserData({
          firstname: '',
          lastname: '',
          email: '',
          userType: '',
          password: ''
        })
        showToaster(data.status, data.message)
      }
    } catch (e) {
      handleErrors(e)
    } finally {
      setIsModifying(false)
    }
  }

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    return users.filter(user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.branch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userType?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = users.length;
    const admins = users.filter(u => u.userType === USERTYPES.Admin || u.userType === USERTYPES.Global).length;
    const standard = users.filter(u => u.userType === USERTYPES.Standard).length;
    const organizations = new Set(users.map(u => u.branch).filter(Boolean)).size;

    return { total, admins, standard, organizations };
  }, [users]);

  const getUserTypeVariant = (userType) => {
    if (userType === USERTYPES.Admin || userType === USERTYPES.Global) return 'danger';
    if (userType === USERTYPES.Organization) return 'warning';
    return 'primary';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case USER_STATUS.PendingEmail:
        return <ModernBadge variant="warning" icon={Mail}>Email Pending</ModernBadge>;
      case USER_STATUS.PendingApproval:
        return <ModernBadge variant="warning" icon={Clock}>Pending Approval</ModernBadge>;
      case USER_STATUS.Active:
        return <ModernBadge variant="success" icon={CheckCircle}>Active</ModernBadge>;
      case USER_STATUS.Disabled:
        return <ModernBadge variant="danger" icon={XCircle}>Disabled</ModernBadge>;
      default:
        return <ModernBadge variant="secondary">Unknown</ModernBadge>;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50" style={{ margin: '-2.5rem', marginTop: '-6rem', padding: '2rem', paddingTop: '4rem', position: 'relative' }}>
      <div className="w-full">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <p className="text-gray-600 mt-1">Manage users and their authority over reports</p>
            </div>
            {user.userType === USERTYPES.Admin && (
              <ModernButton
                variant="primary"
                icon={Plus}
                size="md"
                onClick={() => setAddModal(true)}
              >
                Add User
              </ModernButton>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <ModernStatCard
            title="Total Users"
            value={stats.total.toLocaleString()}
            icon={Users}
            iconBgColor="bg-blue-500"
          />
          <ModernStatCard
            title="Administrators"
            value={stats.admins.toLocaleString()}
            icon={Shield}
            iconBgColor="bg-red-500"
          />
          <ModernStatCard
            title="Standard Users"
            value={stats.standard.toLocaleString()}
            icon={UserCheck}
            iconBgColor="bg-green-500"
          />
          <ModernStatCard
            title="Organizations"
            value={stats.organizations.toLocaleString()}
            icon={Building2}
            iconBgColor="bg-purple-500"
          />
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, email, organization, or user type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Users Table */}
        <ModernCard
          title="All Users"
          subtitle={`${filteredUsers.length} ${filteredUsers.length === 1 ? 'user' : 'users'} found`}
          icon={Users}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Type
                  </th>
                  {user?.userType === USERTYPES.Admin && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((userData, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 font-semibold text-sm">
                              {userData.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{userData.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          {userData.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center">
                          <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                          {userData.branch || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {userData.status === 1 ? (
                          userData.userType === USERTYPES.Standard ? (
                            <ModernBadge variant="warning">Pending</ModernBadge>
                          ) : (
                            <ModernBadge variant="success">Active</ModernBadge>
                          )
                        ) : userData.status === 2 ? (
                          <ModernBadge variant="success">Active</ModernBadge>
                        ) : userData.status === 3 ? (
                          <ModernBadge variant="danger">Disabled</ModernBadge>
                        ) : (
                          <ModernBadge variant="secondary">Email Pending</ModernBadge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ModernBadge variant={getUserTypeVariant(userData.userType)}>
                          {userData.userType}
                        </ModernBadge>
                      </td>
                      {user?.userType === USERTYPES.Admin && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                          {userData.status === 1 && userData.userType === USERTYPES.Standard && (
                            <ModernButton
                              variant="ghost"
                              size="sm"
                              icon={UserCheck}
                              onClick={async () => {
                                try {
                                  setIsModifying(true);
                                  const { data } = await axiosInstance.put(
                                    `${ApiNames.USER_UPDATE}/${userData._id}`,
                                    { status: 2 },
                                    {
                                      headers: {
                                        'If-Match': userData.__v,
                                        'Content-Type': 'application/json',
                                      },
                                    }
                                  );
                                  getUserList();
                                  showToaster(data.status, 'User approved successfully');

                                } catch (error) {
                                  handleErrors(error);
                                } finally {
                                  setIsModifying(false);
                                }
                              }}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              Approve
                            </ModernButton>
                          )}

                          {userData.status === 2 && userData.userType === USERTYPES.Standard && (
                            <ModernButton
                              variant="ghost"
                              size="sm"
                              icon={Ban}
                              onClick={async () => {
                                try {
                                  setIsModifying(true);
                                  const { data } = await axiosInstance.put(
                                    `${ApiNames.USER_UPDATE}/${userData._id}`,
                                    { status: 3 },
                                    {
                                      headers: {
                                        'If-Match': userData.__v,
                                        'Content-Type': 'application/json',
                                      },
                                    }
                                  );
                                  getUserList();
                                  showToaster(data.status, 'User disabled successfully');

                                } catch (error) {
                                  handleErrors(error);
                                } finally {
                                  setIsModifying(false);
                                }
                              }}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            >
                              Disable
                            </ModernButton>
                          )}

                          {userData.status === 3 && userData.userType === USERTYPES.Standard && (
                            <ModernButton
                              variant="ghost"
                              size="sm"
                              icon={CheckCircle}
                              onClick={async () => {
                                try {
                                  setIsModifying(true);
                                  const { data } = await axiosInstance.put(
                                    `${ApiNames.USER_UPDATE}/${userData._id}`,
                                    { status: 2 },
                                    {
                                      headers: {
                                        'If-Match': userData.__v,
                                        'Content-Type': 'application/json',
                                      },
                                    }
                                  );
                                  getUserList();
                                  showToaster(data.status, 'User enabled successfully');

                                } catch (error) {
                                  handleErrors(error);
                                } finally {
                                  setIsModifying(false);
                                }
                              }}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              Enable
                            </ModernButton>
                          )}
                          <ModernButton
                            variant="ghost"
                            size="sm"
                            icon={Edit2}
                            onClick={() => handleClick(1, userData)}
                          >
                            Edit
                          </ModernButton>
                          <ModernButton
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            onClick={() => handleClick(2, userData)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Delete
                          </ModernButton>
                          <ModernButton
                            variant="ghost"
                            size="sm"
                            icon={Shield}
                            onClick={() => handleClick(3, userData)}
                          >
                            Access
                          </ModernButton>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold">{indexOfFirstUser + 1}</span> to{' '}
                  <span className="font-semibold">{Math.min(indexOfLastUser, filteredUsers.length)}</span> of{' '}
                  <span className="font-semibold">{filteredUsers.length}</span> users
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === page
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                          }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </ModernCard>

        {/* Modals */}
        <EditModal
          modal={modal}
          setModal={setModal}
          handleSubmit={handleEditSubmit}
          editUserData={editUserData}
          setEditUserData={setEditUserData}
          isModifying={isModifying}
        />

        <AddModal
          modal={addModal}
          setModal={setAddModal}
          handleSubmit={handleAddSubmit}
          addUserData={addUserData}
          setAddUserData={setAddUserData}
          isModifying={isModifying}
        />
      </div >
    </div >
  )
}

export default connect(null, { setBreadcrumbItems })(UserManagement)
